import { useMemo } from 'react'
import * as React from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import TextLink from 'LuxKit/TextLink'
import LineCrownIcon from 'LuxKit/Icons/line/LineCrownIcon'
import LineChairWithAirplaneIcon from 'LuxKit/Icons/line/LineChairWithAirplaneIcon'
import LineTaxiIcon from 'LuxKit/Icons/line/LineTaxiIcon'
import LineShieldCheckIcon from 'LuxKit/Icons/line/LineShieldCheckIcon'
import getLoyaltyTierDisplayName from 'loyalty/selectors/getLoyaltyTierDisplayName'
import useLoyaltyConfig from 'loyalty/hooks/useLoyaltyConfig'
import { useAppSelector } from 'hooks/reduxHooks'
import getLoyaltyTierThresholds, { getTierThreshold } from 'loyalty/selectors/getLoyaltyTierThresholds'
import FormatDateTime from 'components/utils/Formatters/FormatDateTime'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { tierGradientBackgroundStyle, tierNormalContrastColorStyle } from 'styles/utils/tierStyles'
import Clickable from 'components/Common/Clickable'

const CardContainer = styled.div`
  width: 100%;
  min-width: 0;
  display: flex;
  flex-direction: column;
  border-radius: ${rem(8)};
  overflow: hidden;
  align-self: stretch;
  box-shadow:
    0 ${rem(1)} ${rem(1.5)} rgba(0, 0, 0, 0.07),
    0 ${rem(1.5)} ${rem(5)} rgba(0, 0, 0, 0.05),
    0 ${rem(5)} ${rem(10)} rgba(0, 0, 0, 0.05),
    0 ${rem(15)} ${rem(32)} rgba(0, 0, 0, 0.04);

  ${mediaQueryUp.tablet} {
    max-width: ${rem(383)};
  }
`

const CardHeader = styled.div`
  padding: ${rem(14)} ${rem(16)};
  ${tierGradientBackgroundStyle}

  ${mediaQueryUp.desktop} {
    padding: ${rem(16)} ${rem(24)};
  }
`

const HeaderText = styled.span`
  font-family: ${props => props.theme.font.primary.family};
  font-size: ${rem(16)};
  font-weight: ${props => props.theme.font.primary.weight.bold};
  line-height: ${rem(20)};
  ${tierNormalContrastColorStyle}

  ${mediaQueryUp.tablet} {
    font-size: ${rem(18)};
    line-height: ${rem(22)};
  }
`

const CardBody = styled.div`
  background: ${props => props.theme.palette.neutral.surface.normalBackground};
  padding: ${rem(14)} ${rem(16)};
  display: flex;
  flex-direction: column;
  gap: ${rem(8)};

  ${mediaQueryUp.desktop} {
    padding: ${rem(16)} ${rem(24)};
    gap: ${rem(10)};
  }
`

const HeaderToggle = styled.div`
  display: inline-flex;
  align-items: stretch;
  background: rgba(0, 0, 0, 0.15);
  border-radius: ${rem(6)};
  padding: ${rem(3)};
  gap: ${rem(2)};
`

const HeaderToggleOption = styled(Clickable)`
  font-family: ${props => props.theme.font.primary.family};
  font-size: ${rem(13)};
  line-height: ${rem(18)};
  padding: ${rem(5)} ${rem(10)};
  border-radius: ${rem(4)};
  transition: background 150ms, opacity 150ms;
  ${tierNormalContrastColorStyle}

  &.is-active {
    font-weight: ${props => props.theme.font.primary.weight.bold};
    background: rgba(255, 255, 255, 0.3);
    opacity: 1;
  }

  &.is-inactive {
    font-weight: ${props => props.theme.font.primary.weight.regular};
    opacity: 0.7;
  }

  ${mediaQueryUp.tablet} {
    font-size: ${rem(14)};
    line-height: ${rem(20)};
    padding: ${rem(5)} ${rem(12)};
  }
`

const RequirementsText = styled.p`
  font-family: ${props => props.theme.font.primary.family};
  font-size: ${rem(12)};
  font-weight: ${props => props.theme.font.primary.weight.regular};
  line-height: ${rem(16)};
  color: ${props => props.theme.palette.neutral.surface.normalContrast};
  margin: 0;

  ${mediaQueryUp.tablet} {
    font-size: ${rem(14)};
    line-height: ${rem(20)};
  }
`

const BoldText = styled.span`
  font-weight: ${props => props.theme.font.primary.weight.bold};
`

const ViewDetailsLinkSmall = styled(TextLink)`
  && {
    display: inline-flex;
    margin-top: ${rem(4)};
    color: ${props => props.theme.palette.neutral.surface.normalContrast};
  }

  ${mediaQueryUp.tablet} {
    && {
      display: none;
    }
  }
`

const ViewDetailsLinkMedium = styled(TextLink)`
  && {
    display: none;
    margin-top: ${rem(4)};
    color: ${props => props.theme.palette.neutral.surface.normalContrast};
  }

  ${mediaQueryUp.tablet} {
    && {
      display: inline-flex;
    }
  }
`

const BenefitsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(6)};
  font-size: ${rem(12)};

  ${mediaQueryUp.tablet} {
    gap: ${rem(8)};
    font-size: ${rem(14)};
  }
`

const BenefitItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(6)};

  ${mediaQueryUp.tablet} {
    gap: ${rem(8)};
  }
`

const BenefitText = styled.span`
  font-family: ${props => props.theme.font.primary.family};
  font-size: ${rem(12)};
  font-weight: ${props => props.theme.font.primary.weight.regular};
  line-height: ${rem(16)};
  color: ${props => props.theme.palette.neutral.surface.normalContrast};

  ${mediaQueryUp.tablet} {
    font-size: ${rem(14)};
    line-height: ${rem(20)};
  }
`

const BenefitIconWrapper = styled.div`
  border-radius: ${props => props.theme.borderRadius.round};
  width: ${rem(20)};
  height: ${rem(20)};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  ${mediaQueryUp.tablet} {
    width: ${rem(24)};
    height: ${rem(24)};
  }

  &.tier-bronze {
    background: ${props => props.theme.palette.product.loyalty.bronze.darkBackground};
  }

  &.tier-silver {
    background: ${props => props.theme.palette.product.loyalty.silver.darkBackground};
  }

  &.tier-gold {
    background: ${props => props.theme.palette.product.loyalty.gold.darkForeground};
  }

  &.tier-platinum {
    background: ${props => props.theme.palette.product.loyalty.platinum.darkBackground};
  }
`

interface TierBenefit {
  icon: React.ReactElement
  label: string
}

interface Props {
  /**
   * The tier to display benefits for
   */
  tier: App.LoyaltyTier
  /**
   * Whether this is for retaining current tier (true) or unlocking next tier (false)
   */
  isRetain?: boolean
  /**
   * Whether the member has already retained their Platinum tier before the membership year ends
   * If true, the header text will be "Congratulations!"
   * If false, the header text will be "To retain [tierName]"
   */
  hasRetainedTier?: boolean
  creditsRemaining: number
  nightsRemaining: number
  /**
   * The end of the member's current membership year. Used as the "by" date in unlock mode.
   */
  membershipYearEndDate?: string
  /**
   * The date the member's tier is reviewed. Used as the "by" date in retain mode and the
   * "until" date in the platinum-retain congratulations card.
   */
  tierReviewDate?: string
  onViewDetails?: () => void
  /**
   * Whether to show the retain/upgrade toggle (only for at-risk Silver/Gold members)
   */
  showToggle?: boolean
  /**
   * Whether the toggle is currently showing the upgrade view
   */
  showUpgradeView?: boolean
  /**
   * Called when the user switches between retain and upgrade views
   */
  onToggleView?: (showUpgrade: boolean) => void
  /**
   * Display name of the current tier (for the retain toggle option label)
   */
  currentTierName?: string
  /**
   * Display name of the next tier (for the upgrade toggle option label)
   */
  nextTierName?: string
  /**
   * For retain mode only: which membership year contains the tier review.
   * - `'current-year'` (default): tier review at end of current membership year. Show calculated remaining (bolded).
   * - `'next-year'`: member upgraded mid-year and the tier review is in the *next* membership year. Show absolute SC/Nights requirements (not bolded).
   */
  retainVariant?: 'current-year' | 'next-year'
}

function LoyaltyHeroUnlockTierCard({
  tier,
  isRetain = false,
  hasRetainedTier = false,
  creditsRemaining,
  nightsRemaining,
  membershipYearEndDate,
  tierReviewDate,
  onViewDetails,
  showToggle = false,
  showUpgradeView = false,
  onToggleView,
  currentTierName,
  nextTierName,
  retainVariant = 'current-year',
}: Props) {
  const intl = useIntl()
  const [loyaltyConfig] = useLoyaltyConfig()
  const tierThresholds = useAppSelector(getLoyaltyTierThresholds)
  const tierName = getLoyaltyTierDisplayName(tier)

  const silverRoomUpgradeLabel = intl.formatMessage({
    id: 'eZtS3j26baFSBRXzUjue',
    defaultMessage: '1x free room or package upgrade',
    description: 'Silver tier benefit: number of free room or package upgrades',
  })
  const silverLoungePassLabel = intl.formatMessage({
    id: '4qFdUSk0ut7WMGkK8Isu',
    defaultMessage: '2x free airport lounge passes',
    description: 'Silver tier benefit: number of free airport lounge passes',
  })
  const silverInsuranceLabel = intl.formatMessage({
    id: 'T0LxmmCqgQpmaYM6EEtl',
    defaultMessage: '5% travel protection discount',
    description: 'Silver tier benefit: percentage discount on travel insurance',
  })
  const goldRoomUpgradeLabel = intl.formatMessage({
    id: 'wAfKl-BcPVMPtQfCWLWG',
    defaultMessage: '2x free room or package upgrades',
    description: 'Gold tier benefit: number of free room or package upgrades',
  })
  const goldLoungePassLabel = intl.formatMessage({
    id: 'OYnVWB_hAmzm9CF_YzUy',
    defaultMessage: '2x free airport lounge passes',
    description: 'Gold tier benefit: number of free airport lounge passes',
  })
  const goldTransferLabel = intl.formatMessage({
    id: 'myl5aY7sVx-kCzQdWbht',
    defaultMessage: '2x free airport chauffeur service',
    description: 'Gold tier benefit: number of free airport chauffeur transfers',
  })
  const platinumRoomUpgradeLabel = intl.formatMessage({
    id: 'xmDpDkzWNZK7z21rSPUh',
    defaultMessage: '4x free room or package upgrades',
    description: 'Platinum tier benefit: number of free room or package upgrades',
  })
  const platinumLoungePassLabel = intl.formatMessage({
    id: 'SlQqALxI58IltN__mqAC',
    defaultMessage: '2x free airport lounge passes',
    description: 'Platinum tier benefit: number of free airport lounge passes',
  })
  const platinumTransferLabel = intl.formatMessage({
    id: 'NCsDLxaDWFIY1ULFBqiS',
    defaultMessage: 'Unlimited free airport chauffeur service',
    description: 'Platinum tier benefit: unlimited free airport chauffeur transfers',
  })

  const benefits = useMemo(() => {
    if (!loyaltyConfig) return []
    const enabledBenefits = loyaltyConfig.regionEnabledBenefits
    const result: Array<TierBenefit> = []

    if (tier === 'silver') {
      if (enabledBenefits.hotel_upgrade) {
        result.push({ icon: <LineCrownIcon colour="dark-contrast" size={15} />, label: silverRoomUpgradeLabel })
      }
      if (enabledBenefits.lounge_pass) {
        result.push({ icon: <LineChairWithAirplaneIcon colour="dark-contrast" size={15} />, label: silverLoungePassLabel })
      }
      if (enabledBenefits.discounted_insurance) {
        result.push({ icon: <LineShieldCheckIcon colour="dark-contrast" size={15} />, label: silverInsuranceLabel })
      }
    } else if (tier === 'gold') {
      if (enabledBenefits.hotel_upgrade) {
        result.push({ icon: <LineCrownIcon colour="dark-contrast" size={15} />, label: goldRoomUpgradeLabel })
      }
      if (enabledBenefits.lounge_pass) {
        result.push({ icon: <LineChairWithAirplaneIcon colour="dark-contrast" size={15} />, label: goldLoungePassLabel })
      }
      if (enabledBenefits.airport_transfer) {
        result.push({ icon: <LineTaxiIcon colour="dark-contrast" size={15} />, label: goldTransferLabel })
      }
    } else if (tier === 'platinum') {
      if (enabledBenefits.hotel_upgrade) {
        result.push({ icon: <LineCrownIcon colour="dark-contrast" size={15} />, label: platinumRoomUpgradeLabel })
      }
      if (enabledBenefits.lounge_pass) {
        result.push({ icon: <LineChairWithAirplaneIcon colour="dark-contrast" size={15} />, label: platinumLoungePassLabel })
      }
      if (enabledBenefits.airport_transfer) {
        result.push({ icon: <LineTaxiIcon colour="dark-contrast" size={15} />, label: platinumTransferLabel })
      }
    }

    return result
  }, [tier, loyaltyConfig, silverRoomUpgradeLabel, silverLoungePassLabel, silverInsuranceLabel, goldRoomUpgradeLabel, goldLoungePassLabel, goldTransferLabel, platinumRoomUpgradeLabel, platinumLoungePassLabel, platinumTransferLabel])

  const hasCredits = creditsRemaining > 0
  const hasNights = nightsRemaining > 0

  // Determine header and body text based on retain status
  const congratulationsHeader = intl.formatMessage({
    id: 'BZdjhoiOJyIPmC3g2UVQ',
    defaultMessage: 'Congratulations!',
    description: 'Header shown when user has successfully retained their loyalty tier',
  })
  const toRetainHeader = intl.formatMessage({
    id: '_Qa0aQaYTBtMzj4n5pLA',
    defaultMessage: 'To retain {tierName}',
    description: 'Header indicating requirements to retain current loyalty tier status',
  }, { tierName })
  const toUnlockHeader = intl.formatMessage({
    id: 'LELHCBxctftbQv7ojORs',
    defaultMessage: 'To unlock {tierName}',
    description: 'Header indicating requirements to unlock next loyalty tier',
  }, { tierName })

  const formattedTierReviewDate = tierReviewDate ? <FormatDateTime value={tierReviewDate} format="dmy-casual-short" /> : undefined
  const formattedMembershipYearEndDate = membershipYearEndDate ? <FormatDateTime value={membershipYearEndDate} format="dmy-casual-short" /> : undefined
  const boldTag = (chunks: React.ReactNode) => <BoldText>{chunks}</BoldText>
  const formatRequirementCount = (count: number) => intl.formatNumber(count)
  const formatStatusCreditsCountLabel = (creditsCount: number) => intl.formatMessage(
    {
      id: 'h2ih7p2-OQXQ_tf9OR4J',
      defaultMessage: '{creditsCount, plural, one {{formattedCreditsCount} Status Credit} other {{formattedCreditsCount} Status Credits}}',
      description: 'Status credit count needed for a loyalty tier requirement',
    },
    { creditsCount, formattedCreditsCount: formatRequirementCount(creditsCount) },
  )
  const formatNightCountLabel = (nightsCount: number) => intl.formatMessage(
    {
      id: '-WnrWHXCdYkeyhTu19qf',
      defaultMessage: '{nightsCount, plural, one {{formattedNightsCount} Night} other {{formattedNightsCount} Nights}}',
      description: 'Night count label needed for a loyalty tier requirement',
    },
    { nightsCount, formattedNightsCount: formatRequirementCount(nightsCount) },
  )

  function renderCongratulations(): React.ReactNode {
    if (formattedTierReviewDate) {
      return <FormattedMessage
        id="g_dKnpKiiuRMp7DcLyym"
        defaultMessage="You have retained {tierName} and can enjoy these benefits until {expiryDate}:"
        description="Body text when user has retained their loyalty tier, showing expiry date"
        values={{ tierName, expiryDate: formattedTierReviewDate }}
      />
    }
    return <FormattedMessage
      id="wm7aJ8GCXIBp63AyNkOm"
      defaultMessage="You have retained {tierName} and can enjoy these benefits:"
      description="Body text when user has retained their loyalty tier, no expiry"
      values={{ tierName }}
    />
  }

  function renderRetainNextYear(): React.ReactNode {
    const absoluteThreshold = tierThresholds ? getTierThreshold(tierThresholds, tier) : undefined
    const absoluteCredits = absoluteThreshold?.statusCredits ?? 0
    const absoluteNights = absoluteThreshold?.nights ?? 0
    const creditsCountLabel = formatStatusCreditsCountLabel(absoluteCredits)
    const nightsCountLabel = formatNightCountLabel(absoluteNights)
    if (formattedTierReviewDate) {
      return <FormattedMessage
        id="yT4ZF7jRVYu6V8bd6Fkb"
        defaultMessage="Within your next membership year, you will need {creditsCountLabel} and {nightsCountLabel} by {expiryDate} to retain these {tierName} benefits:"
        description="Retain tier next-year variant: absolute requirements with tier review date for upgraded members whose review falls in the following membership year"
        values={{ creditsCountLabel, nightsCountLabel, expiryDate: formattedTierReviewDate, tierName }}
      />
    }
    return <FormattedMessage
      id="ApcfY1C9Y-S_KBxSxgbr"
      defaultMessage="Within your next membership year, you will need {creditsCountLabel} and {nightsCountLabel} to retain these {tierName} benefits:"
      description="Retain tier next-year variant: absolute requirements without expiry for upgraded members whose review falls in the following membership year"
      values={{ creditsCountLabel, nightsCountLabel, tierName }}
    />
  }

  function renderRetainCurrentYear(): React.ReactNode {
    if (hasCredits && hasNights && formattedTierReviewDate) {
      const creditsCountLabel = formatStatusCreditsCountLabel(creditsRemaining)
      const nightsCountLabel = formatNightCountLabel(nightsRemaining)
      return <FormattedMessage
        id="iWRc3eO1naDu9ni_xsaC"
        defaultMessage="You need <bold>{creditsCountLabel}</bold> and <bold>{nightsCountLabel}</bold> by {expiryDate} to retain these {tierName} benefits:"
        description="Retain tier: credits and nights needed with expiry date"
        values={{ creditsCountLabel, nightsCountLabel, expiryDate: formattedTierReviewDate, tierName, bold: boldTag }}
      />
    }
    if (hasCredits && hasNights) {
      const creditsCountLabel = formatStatusCreditsCountLabel(creditsRemaining)
      const nightsCountLabel = formatNightCountLabel(nightsRemaining)
      return <FormattedMessage
        id="np4Zf24KdSB6By6igkqk"
        defaultMessage="You need <bold>{creditsCountLabel}</bold> and <bold>{nightsCountLabel}</bold> to retain these {tierName} benefits:"
        description="Retain tier: credits and nights needed, no expiry in the loyalty program section"
        values={{ creditsCountLabel, nightsCountLabel, tierName, bold: boldTag }}
      />
    }
    if (hasCredits && formattedTierReviewDate) {
      const creditsCountLabel = formatStatusCreditsCountLabel(creditsRemaining)
      return <FormattedMessage
        id="29Ez8tR4KjCn7lQJ8fGj"
        defaultMessage="You need <bold>{creditsCountLabel}</bold> by {expiryDate} to retain these {tierName} benefits:"
        description="Retain tier: credits needed with expiry date in the loyalty section"
        values={{ creditsCountLabel, expiryDate: formattedTierReviewDate, tierName, bold: boldTag }}
      />
    }
    if (hasCredits) {
      const creditsCountLabel = formatStatusCreditsCountLabel(creditsRemaining)
      return <FormattedMessage
        id="b8vGyvftT3p21iw5Appq"
        defaultMessage="You need <bold>{creditsCountLabel}</bold> to retain these {tierName} benefits:"
        description="Retain tier: credits needed, no expiry in the loyalty program section"
        values={{ creditsCountLabel, tierName, bold: boldTag }}
      />
    }
    if (formattedTierReviewDate) {
      const nightsCountLabel = formatNightCountLabel(nightsRemaining)
      return <FormattedMessage
        id="F49LyRppBnd7e7sGNbss"
        defaultMessage="You need <bold>{nightsCountLabel}</bold> by {expiryDate} to retain these {tierName} benefits:"
        description="Retain tier: nights needed with expiry date in the loyalty section"
        values={{ nightsCountLabel, expiryDate: formattedTierReviewDate, tierName, bold: boldTag }}
      />
    }
    const nightsCountLabel = formatNightCountLabel(nightsRemaining)
    return <FormattedMessage
      id="a4QNtNUU-cwdO4SbqC7k"
      defaultMessage="You need <bold>{nightsCountLabel}</bold> to retain these {tierName} benefits:"
      description="Retain tier: nights needed, no expiry in the loyalty program section"
      values={{ nightsCountLabel, tierName, bold: boldTag }}
    />
  }

  function renderUnlock(): React.ReactNode {
    if (hasCredits && hasNights && formattedMembershipYearEndDate) {
      const creditsCountLabel = formatStatusCreditsCountLabel(creditsRemaining)
      const nightsCountLabel = formatNightCountLabel(nightsRemaining)
      return <FormattedMessage
        id="WX6aOtoIUe70ZKhmW-E9"
        defaultMessage="You need <bold>{creditsCountLabel}</bold> and <bold>{nightsCountLabel}</bold> by {expiryDate} to unlock these {tierName} benefits:"
        description="Unlock tier: credits and nights needed with expiry date"
        values={{ creditsCountLabel, nightsCountLabel, expiryDate: formattedMembershipYearEndDate, tierName, bold: boldTag }}
      />
    }
    if (hasCredits && hasNights) {
      const creditsCountLabel = formatStatusCreditsCountLabel(creditsRemaining)
      const nightsCountLabel = formatNightCountLabel(nightsRemaining)
      return <FormattedMessage
        id="lnHDmnX4mmCG2R962NWs"
        defaultMessage="You need <bold>{creditsCountLabel}</bold> and <bold>{nightsCountLabel}</bold> to unlock these {tierName} benefits:"
        description="Unlock tier: credits and nights needed, no expiry in the loyalty program section"
        values={{ creditsCountLabel, nightsCountLabel, tierName, bold: boldTag }}
      />
    }
    if (hasCredits && formattedMembershipYearEndDate) {
      const creditsCountLabel = formatStatusCreditsCountLabel(creditsRemaining)
      return <FormattedMessage
        id="3X_n7VuzHb0JvNrfrYua"
        defaultMessage="You need <bold>{creditsCountLabel}</bold> by {expiryDate} to unlock these {tierName} benefits:"
        description="Unlock tier: credits needed with expiry date in the loyalty section"
        values={{ creditsCountLabel, expiryDate: formattedMembershipYearEndDate, tierName, bold: boldTag }}
      />
    }
    if (hasCredits) {
      const creditsCountLabel = formatStatusCreditsCountLabel(creditsRemaining)
      return <FormattedMessage
        id="gugXhFMAHZ4tY0Ukb_6u"
        defaultMessage="You need <bold>{creditsCountLabel}</bold> to unlock these {tierName} benefits:"
        description="Unlock tier: credits needed, no expiry in the loyalty program section"
        values={{ creditsCountLabel, tierName, bold: boldTag }}
      />
    }
    if (formattedMembershipYearEndDate) {
      const nightsCountLabel = formatNightCountLabel(nightsRemaining)
      return <FormattedMessage
        id="NAxrqfjBmSOOloI9RCUF"
        defaultMessage="You need <bold>{nightsCountLabel}</bold> by {expiryDate} to unlock these {tierName} benefits:"
        description="Unlock tier: nights needed with expiry date in the loyalty section"
        values={{ nightsCountLabel, expiryDate: formattedMembershipYearEndDate, tierName, bold: boldTag }}
      />
    }
    const nightsCountLabel = formatNightCountLabel(nightsRemaining)
    return <FormattedMessage
      id="53nsbU1U-1HIvfBs_944"
      defaultMessage="You need <bold>{nightsCountLabel}</bold> to unlock these {tierName} benefits:"
      description="Unlock tier: nights needed, no expiry in the loyalty program section"
      values={{ nightsCountLabel, tierName, bold: boldTag }}
    />
  }

  let headerText: string
  let bodyText: React.ReactNode
  if (hasRetainedTier) {
    headerText = congratulationsHeader
    bodyText = renderCongratulations()
  } else if (isRetain && retainVariant === 'next-year') {
    headerText = toRetainHeader
    bodyText = renderRetainNextYear()
  } else if (isRetain) {
    headerText = toRetainHeader
    bodyText = renderRetainCurrentYear()
  } else {
    headerText = toUnlockHeader
    bodyText = renderUnlock()
  }

  if (!loyaltyConfig) return null

  const viewAllDetailsLinkText = intl.formatMessage({
    id: 'twSX_HWMgnXarKdaxb9d',
    defaultMessage: 'View all details',
    description: 'Link text to view full tier benefit details in the loyalty section',
  })

  return (
    <CardContainer>
      <CardHeader className={`tier-${tier}`}>
        {showToggle && currentTierName && nextTierName && (
          <HeaderToggle>
            <HeaderToggleOption
              className={`tier-${tier} ${!showUpgradeView ? 'is-active' : 'is-inactive'}`}
              onClick={() => onToggleView?.(false)}
            >
              {intl.formatMessage(
                { id: '9ZN5FHO5pQctKoGZ26Aw', defaultMessage: 'Retain {tierName}', description: 'Toggle option to view current tier retention requirements' },
                { tierName: currentTierName },
              )}
            </HeaderToggleOption>
            <HeaderToggleOption
              className={`tier-${tier} ${showUpgradeView ? 'is-active' : 'is-inactive'}`}
              onClick={() => onToggleView?.(true)}
            >
              {intl.formatMessage(
                { id: 'NilqMdlNHrDTNigNu1IR', defaultMessage: 'Unlock {tierName}', description: 'Toggle option to view next tier upgrade requirements' },
                { tierName: nextTierName },
              )}
            </HeaderToggleOption>
          </HeaderToggle>
        )}
        {!(showToggle && currentTierName && nextTierName) && (
          <HeaderText className={`tier-${tier}`}>
            {headerText}
          </HeaderText>
        )}
      </CardHeader>
      <CardBody>
        <RequirementsText>
          {bodyText}
        </RequirementsText>

        <BenefitsContainer>
          {benefits.map((benefit) => (
            <BenefitItem key={benefit.label}>
              <BenefitIconWrapper className={`tier-${tier}`}>
                {benefit.icon}
              </BenefitIconWrapper>
              <BenefitText>
                {benefit.label}
              </BenefitText>
            </BenefitItem>
          ))}

          {onViewDetails && (
            <>
              <ViewDetailsLinkSmall size="small" weight="bold" underline onClick={onViewDetails}>
                {viewAllDetailsLinkText}
              </ViewDetailsLinkSmall>
              <ViewDetailsLinkMedium size="medium" weight="bold" underline onClick={onViewDetails}>
                {viewAllDetailsLinkText}
              </ViewDetailsLinkMedium>
            </>
          )}
        </BenefitsContainer>
      </CardBody>
    </CardContainer>
  )
}

export default LoyaltyHeroUnlockTierCard
